import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {FractionalUnitsType} from '@mintdata/mint-kit/api/models/FractionalUnitsType';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AStringEnumConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AEnumConfig';
import {ANumberConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/ANumberConfig';
import {FractionalUnitsStringConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/FractionalUnitsConfig';

export class FractionalUnits
{
    @Property<ANumber>({
        title: 'Value',
        tip: `Value`,
        isRequired: true,
        typeConfig: ANumberConfig,
        typeCtor: ANumber,
    })
    public value:ANumber;

    @Property<FractionalUnitsType>({
        title: 'UnitsType',
        tip: `UnitsType`,
        isRequired: true,
        typeConfig: AStringEnumConfig,
        typeCtor: FractionalUnitsType,
    })
    public unitsType:FractionalUnitsType;

    public constructor(value:ANumber,
                       unitsType:FractionalUnitsType)
    {
        this.value = value;
        this.unitsType = unitsType;
    }

    @Property<AString>({
        title: 'UnitsString',
        tip: `UnitsString`,
        isRequired: true,
        typeConfig: FractionalUnitsStringConfig,
        typeCtor: AString,
    })
    public get unitsString():AString
    {
        const unitsString:string = this.toString();

        return unitsString != null && AString.fromString(unitsString);
    }

    public set unitsString(unitsString:AString)
    {
        if( !unitsString || unitsString.value == null ) return;

        const unitsValueString:string = unitsString.value.trim();

        if( unitsValueString && unitsValueString.length > 0 )
        {
            if( unitsValueString.toLowerCase() === FractionalUnitsType.AUTO.value.toLowerCase() )
            {
                this.value = ANumber.fromNumber(0);
                this.unitsType = FractionalUnitsType.auto();
            }
            else
            {
                const parts:Array<string> = /(^[0-9]*.*[0-9]+)(\D*$)?/.exec(unitsValueString);

                if( parts && parts.length > 1 )
                {
                    const value:ANumber = ANumber.fromNumber(parseFloat(parts[1]));
                    const unitsType:FractionalUnitsType = !parts[2] || parts[2] === FractionalUnitsType.pixels().value.value
                        ? FractionalUnitsType.pixels()
                        : (parts[2] === FractionalUnitsType.percentages().value.value
                            ? FractionalUnitsType.percentages()
                            : (parts[2] === FractionalUnitsType.fractions().value.value
                                ? FractionalUnitsType.fractions()
                                : null));

                    if( value && unitsType )
                    {
                        this.value = value;
                        this.unitsType = unitsType;
                    }
                }
            }
        }
    }

    public toCssString():string
    {
        if( this.unitsType && this.unitsType.value && this.unitsType.value.isEqualTo(FractionalUnitsType.AUTO) )
            return FractionalUnitsType.AUTO.value;

        return this.toString();
    }

    public toString():string
    {
        if( this.unitsType && this.unitsType.value && this.unitsType.value.isEqualTo(FractionalUnitsType.AUTO) )
            return this.unitsType.toString();

        return this.value && this.unitsType ? `${this.value}${this.unitsType}` : null;
    }

    public toValue():string
    {
        return `{"value":${this.value.toValue()}, "unitsType":${this.unitsType.toValue()}}`;
    }

    public toIValue():IValue
    {
        return FractionalUnitsType.pixels().isEqualTo(this.unitsType)
            ? this.value
            : this.unitsString;
    }

    public isAuto():boolean
    {
        return this.unitsType.value.value === 'auto';
    }

    public isEqualTo(other:FractionalUnits):boolean
    {
        return other &&
            other.value &&
            other.value.isEqualTo(this.value)
            && other.unitsType
            && other.unitsType.isEqualTo(this.unitsType);
    }

    public static fromFractionalUnitsString(value:AString):FractionalUnits
    {
        const units:FractionalUnits = FractionalUnits.auto();
        units.unitsString = value;
        return units;
    }

    public static fromPixels(value:number):FractionalUnits
    {
        return new FractionalUnits(new ANumber(value), FractionalUnitsType.pixels());
    }

    public static fromPercentages(value:number):FractionalUnits
    {
        return new FractionalUnits(new ANumber(value), FractionalUnitsType.percentages());
    }

    public static fromFractions(value:number):FractionalUnits
    {
        return new FractionalUnits(new ANumber(value), FractionalUnitsType.fractions());
    }

    public static auto():FractionalUnits
    {
        return new FractionalUnits(new ANumber(0), FractionalUnitsType.auto());
    }

    public static coerce(from:IValue):FractionalUnits | null
    {
        if( from.getType().isEqualTo(Type.String)
            || from.getType().isEqualTo(Type.Number)
            || from.getType().isEqualTo(Type.Integer) )
        {
            return FractionalUnits.fromFractionalUnitsString(AString.fromString(from.toString()));
        }

        return null;
    }
}
